import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/brand/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { capture, captureOnce, EVENTS } from "../analytics/posthog";
import { useAbandonTracker } from "../analytics/useAbandon";

const SECTORS = ["Hostelería", "Gimnasios", "Retail", "Talleres", "Instaladores", "Mantenimiento", "Limpieza", "Estética", "Agencia operativa", "Servicios presenciales", "Otro"];
const EMPLOYEES = ["Solo yo", "1–2", "3–5", "6–10", "11–25", "26–50", "51+"];
const LOCATIONS = ["1", "2-3", "4+"];
const ROLES = ["Operaciones", "Ventas críticas", "Decisiones diarias", "Aprobaciones financieras", "Todo un poco"];
const GOALS = [
  "Quiero que la empresa dependa menos de mí",
  "Me preocupa depender de personas clave",
  "Quiero preparar otra ubicación",
  "Quiero profesionalizar operaciones",
  "Otro",
];

function Chip({ active, children, onClick, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={cn("rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors text-left",
        active ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20")}>
      {children}
    </button>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ name: "", sector: "", employee_count: "", location_count: "", owner_role: "", main_goal: "" });

  const set = (k, v) => setF({ ...f, [k]: v });
  const closeFlow = useAbandonTracker(EVENTS.onboardingAbandoned, () => ({ step: step + 1 }));
  const canNext = [f.name && f.sector, f.employee_count && f.location_count, f.owner_role, f.main_goal][step];

  const finish = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/onboarding", f);
      closeFlow();
      captureOnce(EVENTS.workspaceCreated, data.id, { workspace_id: data.id, sector: f.sector });
      await refresh();
      navigate("/audit", { replace: true });
    } catch (e) {
      capture(EVENTS.onboardingFailed, { status: e?.response?.status || "network" });
      toast.error(apiErr(e)); setLoading(false);
    }
  };

  const next = () => { if (step < 3) setStep(step + 1); else finish(); };

  return (
    <div className="min-h-screen bg-obsidian grid-bg flex flex-col">
      <div className="p-6 flex items-center justify-between">
        <Logo />
        <span className="mono-label text-slate-500">PASO {step + 1} / 4</span>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-xl">
          <div className="mb-6 h-1 rounded-full bg-white/5">
            <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${(step + 1) * 25}%` }} />
          </div>
          <div className="card-arch p-8">
            <div className="halo" />
            <div className="relative">
              {step === 0 && (
                <div className="space-y-5">
                  <h1 className="font-head text-2xl font-bold text-white">Tu empresa</h1>
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400">Nombre de la empresa</label>
                    <Input data-testid="onboarding-name-input" value={f.name} onChange={(e) => set("name", e.target.value)}
                      className="bg-white/[0.03] border-white/10" placeholder="Ej. Talleres Norte" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400">Sector</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {SECTORS.map((s) => <Chip key={s} testId={`onboarding-sector-${s}`} active={f.sector === s} onClick={() => set("sector", s)}>{s}</Chip>)}
                    </div>
                  </div>
                </div>
              )}
              {step === 1 && (
                <div className="space-y-6">
                  <h1 className="font-head text-2xl font-bold text-white">Dimensión</h1>
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400">Nº aproximado de empleados</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {EMPLOYEES.map((s) => <Chip key={s} testId={`onboarding-emp-${s}`} active={f.employee_count === s} onClick={() => set("employee_count", s)}>{s}</Chip>)}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400">Nº de ubicaciones</label>
                    <div className="grid grid-cols-3 gap-2">
                      {LOCATIONS.map((s) => <Chip key={s} testId={`onboarding-loc-${s}`} active={f.location_count === s} onClick={() => set("location_count", s)}>{s}</Chip>)}
                    </div>
                  </div>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-5">
                  <h1 className="font-head text-2xl font-bold text-white">Tu rol</h1>
                  <p className="text-sm text-slate-400">¿Dónde estás más implicado hoy?</p>
                  <div className="grid gap-2">
                    {ROLES.map((s) => <Chip key={s} testId={`onboarding-role-${s}`} active={f.owner_role === s} onClick={() => set("owner_role", s)}>{s}</Chip>)}
                  </div>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-5">
                  <h1 className="font-head text-2xl font-bold text-white">Tu objetivo principal</h1>
                  <div className="grid gap-2">
                    {GOALS.map((s) => <Chip key={s} testId={`onboarding-goal-${s}`} active={f.main_goal === s} onClick={() => set("main_goal", s)}>{s}</Chip>)}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-6 flex items-center justify-between">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white disabled:opacity-30">
              <ArrowLeft className="h-4 w-4" /> Atrás
            </button>
            <Button data-testid="onboarding-next-button" onClick={next} disabled={!canNext || loading}
              className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold px-6">
              {loading ? "Preparando…" : step === 3 ? "Comenzar diagnóstico" : "Continuar"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
